import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Eye, Loader2, RefreshCw } from "lucide-react";

import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import {
  StatusBadge,
  type StatusBadgeVariant,
} from "@/components/shared/StatusBadge";
import { TableCard } from "@/components/shared/TableCard";
import { TablePagination } from "@/components/shared/TablePagination";
import { TableActionsMenu, type TableAction } from "@/components/shared/TableActionsMenu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { usePaginationFromUrl } from "@/hooks/usePaginationFromUrl";
import { cn } from "@/lib/utils";
import { formatDisplayDate, parseLocalDateOnly } from "@/lib/dateFormat";
import {
  canResendDailyReport,
  dailyReportDisplayStatus,
  listDailyReports,
  resendDailyReport,
  type DailyReportListItem,
  type DailyReportStatus,
} from "@/services/dailyReportsService";

function statusBadgeVariant(displayStatus: string): StatusBadgeVariant {
  switch (displayStatus) {
    case "ack_ok":
      return "success";
    case "needs_attention":
      return "danger";
    case "retrying":
      return "warning";
    case "pending":
    case "sent":
      return "neutral";
    default:
      return "neutral";
  }
}

export function DailyReportsPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const pagination = usePaginationFromUrl();
  const [resendingId, setResendingId] = useState<string | null>(null);

  const {
    data,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["daily-reports", pagination.page, pagination.limit],
    queryFn: () =>
      listDailyReports({ page: pagination.page, limit: pagination.limit }),
  });

  useEffect(() => {
    if (data != null) {
      pagination.setTotal(data.total);
    } else if (isError) {
      pagination.setTotal(0);
    }
  }, [data, isError, pagination]);

  useEffect(() => {
    if (isError) {
      toast.error(t("dailyReports.list.loadError"));
    }
  }, [isError, t]);

  const resendMutation = useMutation({
    mutationFn: (id: string) => resendDailyReport(id),
    onMutate: (id: string) => {
      setResendingId(id);
      toast.loading(t("dailyReports.list.resendLoading"), {
        id: `daily-report-resend-${id}`,
      });
    },
    onSettled: () => {
      setResendingId(null);
    },
    onSuccess: async (result, id: string) => {
      queryClient.setQueryData(["daily-reports", result.id], result);
      await queryClient.invalidateQueries({ queryKey: ["daily-reports"] });
      const toastId = `daily-report-resend-${id}`;
      if (result.status === "ack_ok") {
        toast.success(t("dailyReports.list.resendSuccess"), { id: toastId });
      } else if (result.status === "ack_rejected") {
        toast.error(t("dailyReports.list.resendRejectedByDgi"), { id: toastId });
      } else {
        toast.error(t("dailyReports.list.resendSendFailed"), { id: toastId });
      }
    },
    onError: (_err, id: string) => {
      toast.error(t("dailyReports.list.resendError"), {
        id: `daily-report-resend-${id}`,
      });
    },
  });

  const items: DailyReportListItem[] = data?.items ?? [];
  const loading = isLoading;

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        title={t("dailyReports.list.title")}
        description={t("dailyReports.list.subtitle")}
      />
      <TableCard>
        {loading ? (
          <div className="flex min-h-[40vh] items-center justify-center py-12">
            <LoadingLogo size="lg" />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title={t("dailyReports.list.emptyTitle")}
            message={t("dailyReports.list.emptyMessage")}
          />
        ) : (
          <>
            {/* Mobile cards */}
            <div className="space-y-3 py-2 md:hidden">
              {items.map((row) => {
                const reportDateLabel = formatDisplayDate(
                  parseLocalDateOnly(row.fechaResumen),
                  { locale: i18n.language }
                );
                const sentAt = formatDisplayDate(row.createdAt, {
                  locale: i18n.language,
                });
                const actions: TableAction[] = [
                  {
                    label: t("dailyReports.list.viewDetails"),
                    icon: Eye,
                    onClick: () =>
                      navigate(`/dgi/daily-reports/${row.id}`),
                  },
                ];
                if (canResendDailyReport(row.status)) {
                  const isResending = resendingId === row.id;
                  actions.push({
                    label: isResending
                      ? t("dailyReports.list.resending")
                      : t("dailyReports.list.resend"),
                    icon: isResending
                      ? ({ className }: { className?: string }) => (
                          <Loader2
                            className={cn(className, "animate-spin")}
                          />
                        )
                      : RefreshCw,
                    onClick: () => {
                      resendMutation.mutate(row.id);
                    },
                    disabled: isResending,
                  });
                }
                return (
                  <div
                    key={row.id}
                    className="cursor-pointer rounded-lg border bg-card p-4 transition-colors duration-150 hover:bg-muted/50"
                    onClick={() => navigate(`/dgi/daily-reports/${row.id}`)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 space-y-1">
                        <p className="font-medium text-foreground">{reportDateLabel}</p>
                        {(() => {
                          const ds = dailyReportDisplayStatus(row.status, row.retriesExhausted);
                          return (
                            <StatusBadge variant={statusBadgeVariant(ds)}>
                              {t(`dailyReports.status.${ds}`)}
                            </StatusBadge>
                          );
                        })()}
                      </div>
                      <div onClick={(e) => e.stopPropagation()}>
                        <TableActionsMenu actions={actions} />
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span>{t("dailyReports.list.columnDgiEnvio")}: {row.secEnvio}</span>
                      <span>{t("dailyReports.list.columnAttempt")}: {row.attemptNumber}</span>
                      <span>{sentAt}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Desktop table */}
            <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("dailyReports.list.columnReportDate")}</TableHead>
                  <TableHead>{t("dailyReports.list.columnStatus")}</TableHead>
                  <TableHead className="whitespace-nowrap">
                    {t("dailyReports.list.columnDgiEnvio")}
                  </TableHead>
                  <TableHead className="whitespace-nowrap">
                    {t("dailyReports.list.columnAttempt")}
                  </TableHead>
                  <TableHead>{t("dailyReports.list.columnSentAt")}</TableHead>
                  <TableHead className="w-[72px] text-right">
                    {t("dailyReports.list.columnActions")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row) => {
                  const reportDateLabel = formatDisplayDate(
                    parseLocalDateOnly(row.fechaResumen),
                    { locale: i18n.language }
                  );
                  const sentAt = formatDisplayDate(row.createdAt, {
                    locale: i18n.language,
                  });
                  const actions: TableAction[] = [
                    {
                      label: t("dailyReports.list.viewDetails"),
                      icon: Eye,
                      onClick: () =>
                        navigate(`/dgi/daily-reports/${row.id}`),
                    },
                  ];
                  if (canResendDailyReport(row.status)) {
                    const isResending = resendingId === row.id;
                    actions.push({
                      label: isResending
                        ? t("dailyReports.list.resending")
                        : t("dailyReports.list.resend"),
                      icon: isResending
                        ? ({ className }: { className?: string }) => (
                            <Loader2
                              className={cn(className, "animate-spin")}
                            />
                          )
                        : RefreshCw,
                      onClick: () => {
                        resendMutation.mutate(row.id);
                      },
                      disabled: isResending,
                    });
                  }
                  return (
                    <TableRow
                      key={row.id}
                      onRowClick={() =>
                        navigate(`/dgi/daily-reports/${row.id}`)
                      }
                    >
                      <TableCell className="font-medium">
                        {reportDateLabel}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const ds = dailyReportDisplayStatus(row.status, row.retriesExhausted);
                          return (
                            <StatusBadge variant={statusBadgeVariant(ds)}>
                              {t(`dailyReports.status.${ds}`)}
                            </StatusBadge>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="tabular-nums">{row.secEnvio}</TableCell>
                      <TableCell className="tabular-nums text-muted-foreground">
                        {row.attemptNumber}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {sentAt}
                      </TableCell>
                      <TableCell className="text-right">
                        <TableActionsMenu actions={actions} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </div>
            <TablePagination
              pagination={pagination}
              rowsPerPageLabel={t("dailyReports.list.rowsPerPage")}
              pageOfLabel={t("dailyReports.list.pageOf", {
                from:
                  pagination.total === 0
                    ? 0
                    : (pagination.page - 1) * pagination.limit + 1,
                to: Math.min(
                  pagination.page * pagination.limit,
                  pagination.total
                ),
                total: pagination.total,
              })}
              onRefresh={() => void refetch()}
              isRefreshing={isFetching}
            />
          </>
        )}
      </TableCard>
    </div>
  );
}
